import { v4 as uuidv4 } from 'uuid';

import { RouteWaypointEntity, RouteWaypointType } from '../entities';

export interface MapToRouteWaypointsInput {
  orderId?: string | number;
  latitude: string | number;
  longitude: string | number;
  altitude?: string | number;
}

export const mapToRouteWaypoints = (
  items: MapToRouteWaypointsInput[],
  defaultAltitude?: number | string,
): RouteWaypointEntity[] => {
  const size = items.length;

  return items.map((item, idx) => {
    const updatedWp: RouteWaypointEntity = {
      uuid: uuidv4(),
      orderId: item.orderId,
      latitude: item.latitude,
      longitude: item.longitude,
      altitude: item.altitude ?? defaultAltitude ?? 0,
      type: RouteWaypointType.WAYPOINT,
    };


    if (item.orderId) {
      return {
        ...updatedWp,
        type: RouteWaypointType.DESTINATION,
      };
    }

    if (idx === 0 || idx === size - 1) {
      return {
        ...updatedWp,
        type: RouteWaypointType.TAKE_OFF,
      };
    }

    return updatedWp;
  });
};

// The mission route is a round trip [takeoff, ...out..., destination, ...return..., takeoff].
// Editing only deals with the OUTBOUND leg (takeoff -> waypoints -> destination); the return is
// mirrored from it at save time. `getOutboundLeg` slices the outbound portion off a full route.
export const getOutboundLeg = <T extends { orderId?: string | number | null }>(waypoints: T[]): T[] => {
  const destinationIdx = waypoints.findIndex((w) => Boolean(w.orderId));
  return destinationIdx >= 0 ? waypoints.slice(0, destinationIdx + 1) : waypoints;
};

interface HomeCandidate {
  label?: string;
  type?: string | null;
}


export const isHomeWaypoint = (w: HomeCandidate): boolean =>
  w.type === 'HOME' || w.label === 'H';


export const splitHomeLeg = <T extends HomeCandidate>(
  waypoints: T[],
): { homePoint: T | null; routeWaypoints: T[] } => {
  const homePoint = waypoints.find((w) => isHomeWaypoint(w)) ?? null;
  const routeWaypoints = waypoints.filter((w) => !isHomeWaypoint(w));
  return { homePoint, routeWaypoints };
};

interface RouteCandidate extends HomeCandidate {
  orderId?: string | number;
  latitude: string | number;
  longitude: string | number;
}

export const toEditableRoute = <T extends RouteCandidate>(waypoints: T[]): T[] => {
  const { routeWaypoints } = splitHomeLeg(waypoints);
  if (routeWaypoints.length === 0) return [];

  const coordKey = (w: T): string =>
    `${Number(w.latitude).toFixed(6)},${Number(w.longitude).toFixed(6)}`;

  const firstTakeoff = routeWaypoints[0];
  const lastWp = routeWaypoints[routeWaypoints.length - 1];
  const hasReturnTakeoff =
    routeWaypoints.length > 1 && !lastWp.orderId && coordKey(lastWp) === coordKey(firstTakeoff);

  const seen = new Set<string>();
  const unique: T[] = [];
  routeWaypoints.forEach((w) => {
    const key = coordKey(w);
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(w);
  });

  return hasReturnTakeoff ? [...unique, firstTakeoff] : unique;
};

// Maps an OUTBOUND leg (last item is the destination and carries `orderId`) to route waypoints.
// Unlike `mapToRouteWaypoints`, the final item is the destination, not a return-to-takeoff point.
export const mapToOutboundRouteWaypoints = (
  items: MapToRouteWaypointsInput[],
  defaultAltitude?: number | string,
): RouteWaypointEntity[] =>
  items.map((item, idx) => {
    const updatedWp: RouteWaypointEntity = {
      uuid: uuidv4(),
      orderId: item.orderId,
      latitude: item.latitude,
      longitude: item.longitude,
      altitude: item.altitude ?? defaultAltitude ?? 0,
      type: RouteWaypointType.WAYPOINT,
    };

    if (idx === 0) return { ...updatedWp, type: RouteWaypointType.TAKE_OFF };
    if (item.orderId) return { ...updatedWp, type: RouteWaypointType.DESTINATION };
    return updatedWp;
  });
